import { eventBus, EVENTS } from './EventBus'
import { ColumnManager } from './ColumnManager'
import { formatCode } from './formatCode'

export class ClipboardManager {
  constructor() {
    this.clipboard = null
    this.clipboardType = null
  }

  copyRow(rowData, distributionManager, customerCodes) {
    const currentDist = []

    customerCodes.forEach((columnKey) => {
      const originalValue = distributionManager.getOriginalValue(
        rowData,
        columnKey
      )

      const value = distributionManager.getValue(
        rowData.hinmeiCode,
        rowData.niukabango,
        columnKey,
        originalValue
      )

      const strValue = String(value ?? '').trim()
      const saveValue = strValue === '' ? '' : strValue === '0' ? '0' : strValue

      currentDist.push({
        column_key: columnKey,
        delivery_quantity: saveValue
      })
    })

    this.clipboard = {
      type: 'row',
      data: {
        ...JSON.parse(JSON.stringify(rowData)),
        distribution_result: currentDist
      },
      timestamp: Date.now()
    }
    this.clipboardType = 'row'

    eventBus.emit(EVENTS.CLIPBOARD_COPY, {
      type: 'row',
      data: rowData,
      hinmeiCode: rowData.hinmeiCode,
      niukabango: rowData.niukabango
    })

    return true
  }

  copyColumn(columnKey, allRowsData, distributionManager) {
    const columnData = allRowsData.map((row) => {
      const originalValue = distributionManager.getOriginalValue(row, columnKey)

      const value = distributionManager.getValue(
        row.hinmeiCode,
        row.niukabango,
        columnKey,
        originalValue
      )

      const strValue = String(value ?? '').trim()
      const saveValue = strValue === '' ? '' : strValue === '0' ? '0' : strValue

      return {
        hinmeiCode: row.hinmeiCode,
        niukabango: row.niukabango,
        value: saveValue
      }
    })

    this.clipboard = {
      type: 'column',
      columnKey,
      customerCode: ColumnManager.parseColumnKey(columnKey).customerCode,
      data: columnData,
      timestamp: Date.now()
    }
    this.clipboardType = 'column'

    eventBus.emit(EVENTS.CLIPBOARD_COPY, {
      type: 'column',
      columnKey,
      customerCode: ColumnManager.parseColumnKey(columnKey).customerCode,
      data: columnData
    })

    return true
  }

  copyCell(hinmeiCode, niukabango, columnKey, value) {
    this.clipboard = {
      type: 'cell',
      data: { hinmeiCode, niukabango, customerCode: columnKey, value },
      timestamp: Date.now()
    }
    this.clipboardType = 'cell'

    eventBus.emit(EVENTS.CLIPBOARD_COPY, {
      type: 'cell',
      hinmeiCode,
      niukabango,
      customerCode: columnKey,
      value
    })

    return true
  }

  pasteRow(targetRow, distributionManager) {
    if (!this.clipboard || this.clipboard.type !== 'row') return false

    const sourceData = this.clipboard.data
    const rowKey = distributionManager.getRowKey(
      targetRow.hinmeiCode,
      targetRow.niukabango
    )
    const pastedCells = []

    if (sourceData.distribution_result) {
      sourceData.distribution_result.forEach((dist) => {
        const columnKey = String(dist.column_key ?? dist.customer_code ?? '')
        if (!columnKey || columnKey === 'price' || columnKey === 'id') return

        const value = dist.delivery_quantity ?? ''

        const originalValue = distributionManager.getOriginalValue(
          targetRow,
          columnKey
        )
        const beforeValue =
          distributionManager.data[rowKey]?.[columnKey] !== undefined
            ? distributionManager.data[rowKey][columnKey]
            : (originalValue ?? null)

        if (!distributionManager.data[rowKey])
          distributionManager.data[rowKey] = {}

        const newValue =
          value === '' ? null : isNaN(Number(value)) ? null : Number(value)
        distributionManager.data[rowKey][columnKey] = newValue

        pastedCells.push({ columnKey, beforeValue, afterValue: newValue })
      })
    }

    eventBus.emit(EVENTS.CLIPBOARD_PASTE, {
      type: 'row',
      source: {
        hinmeiCode: sourceData.hinmeiCode,
        niukabango: sourceData.niukabango
      },
      target: {
        hinmeiCode: targetRow.hinmeiCode,
        niukabango: targetRow.niukabango
      }
    })

    distributionManager.addLogEntry({
      action: `Pasted row from ${sourceData.hinmeiCode}-${sourceData.niukabango} to ${targetRow.hinmeiCode}-${targetRow.niukabango}`,
      changeData: {
        type: 'row_paste',
        sourceHinmeiCode: sourceData.hinmeiCode,
        sourceNiukabango: sourceData.niukabango,
        targetHinmeiCode: targetRow.hinmeiCode,
        targetNiukabango: targetRow.niukabango,
        pastedCells
      },
      up: () => {
        if (!distributionManager.data[rowKey])
          distributionManager.data[rowKey] = {}
        pastedCells.forEach((cell) => {
          distributionManager.data[rowKey][cell.columnKey] = cell.afterValue
        })
        eventBus.emit(EVENTS.DISTRIBUTION_RESET, distributionManager.data)
      },
      down: () => {
        if (!distributionManager.data[rowKey])
          distributionManager.data[rowKey] = {}
        pastedCells.forEach((cell) => {
          if (cell.beforeValue === null) {
            delete distributionManager.data[rowKey][cell.columnKey]
          } else {
            distributionManager.data[rowKey][cell.columnKey] = cell.beforeValue
          }
        })
        eventBus.emit(EVENTS.DISTRIBUTION_RESET, distributionManager.data)
      }
    })

    return true
  }

  pasteColumnAsNew(
    allRowsData,
    distributionManager,
    columnManager,
    targetColumnKey = null
  ) {
    if (!this.clipboard || this.clipboard.type !== 'column') return null

    const tempCode = `NEW_${Date.now()}`
    const sourceColumnKey = this.clipboard.columnKey

    let success
    let insertPosition = 'end'

    if (!targetColumnKey) {
      success = columnManager.addCustomerColumn(tempCode, '')
    } else {
      // Always insert the new column to the LEFT of the selected (target) column
      success = columnManager.insertCustomerColumnBefore(
        targetColumnKey,
        tempCode,
        ''
      )
      insertPosition = 'before'
    }

    if (!success) return null

    distributionManager.addCustomerToItems(allRowsData, tempCode, '')

    const sourceData = this.clipboard.data
    const pastedCells = []

    sourceData.forEach((sourceCell) => {
      const targetRow = allRowsData.find(
        (row) =>
          row.hinmeiCode === sourceCell.hinmeiCode &&
          String(row.niukabango) === String(sourceCell.niukabango)
      )

      if (targetRow) {
        const rowKey = distributionManager.getRowKey(
          targetRow.hinmeiCode,
          targetRow.niukabango
        )
        if (!distributionManager.data[rowKey])
          distributionManager.data[rowKey] = {}

        const value = sourceCell.value === '' ? null : Number(sourceCell.value)
        distributionManager.data[rowKey][tempCode] = value

        pastedCells.push({
          hinmeiCode: targetRow.hinmeiCode,
          niukabango: targetRow.niukabango,
          value
        })
      }
    })

    eventBus.emit(EVENTS.CLIPBOARD_PASTE, {
      type: 'column',
      sourceColumnKey,
      newColumnCode: tempCode,
      pasteCount: pastedCells.length,
      isNewColumn: true,
      targetColumnKey,
      insertPosition
    })
    const targetColumn = formatCode(targetColumnKey)

    const positionText =
      insertPosition === 'before'
        ? `${targetColumn} の前`
        : insertPosition === 'after'
          ? `${targetColumn} の後`
          : '末尾'

    const customerCode = formatCode(this.clipboard.columnKey)

    distributionManager.addLogEntry({
      action: `[${distributionManager.username}] ${customerCode} の列を新しい列 ${positionText} として貼り付けました（${pastedCells.length} セル）`,
      changeData: {
        type: 'column_paste',
        customerCode: tempCode,
        sourceColumnKey,
        position: insertPosition,
        targetColumnKey,
        pastedCells
      },
      up: () => {
        if (!columnManager.hasCustomerCode(tempCode)) {
          if (insertPosition === 'before') {
            columnManager.insertCustomerColumnBefore(
              targetColumnKey,
              tempCode,
              ''
            )
          } else {
            columnManager.addCustomerColumn(tempCode, '')
          }
          distributionManager.addCustomerToItems(allRowsData, tempCode, '')
        }
        pastedCells.forEach((cell) => {
          const rowKey = distributionManager.getRowKey(
            cell.hinmeiCode,
            cell.niukabango
          )
          if (!distributionManager.data[rowKey])
            distributionManager.data[rowKey] = {}
          distributionManager.data[rowKey][tempCode] = cell.value
        })
        eventBus.emit(EVENTS.DISTRIBUTION_RESET, distributionManager.data)
      },
      down: () => {
        const columnId = `customer_${tempCode}`
        columnManager.removeColumn(columnId)

        distributionManager.originalItems.forEach((item) => {
          if (item.distribution_result) {
            item.distribution_result = item.distribution_result.filter(
              (d) => String(d.customer_code) !== String(tempCode)
            )
          }
        })

        pastedCells.forEach((cell) => {
          const rowKey = distributionManager.getRowKey(
            cell.hinmeiCode,
            cell.niukabango
          )
          if (distributionManager.data[rowKey]) {
            delete distributionManager.data[rowKey][tempCode]
          }
        })
        eventBus.emit(EVENTS.DISTRIBUTION_RESET, distributionManager.data)
      }
    })

    return tempCode
  }

  pasteCell(
    targetHinmeiCode,
    targetNiukabango,
    targetColumnKey,
    distributionManager,
    targetRow
  ) {
    if (!this.clipboard || this.clipboard.type !== 'cell') return false

    const sourceData = this.clipboard.data

    distributionManager.commitChange(
      targetHinmeiCode,
      targetNiukabango,
      targetColumnKey,
      String(sourceData.value)
    )

    eventBus.emit(EVENTS.CLIPBOARD_PASTE, {
      type: 'cell',
      source: sourceData,
      target: {
        hinmeiCode: targetHinmeiCode,
        niukabango: targetNiukabango,
        customerCode: targetColumnKey
      }
    })

    return true
  }

  hasData() {
    return this.clipboard !== null
  }
  getType() {
    return this.clipboardType
  }

  clear() {
    this.clipboard = null
    this.clipboardType = null
    eventBus.emit(EVENTS.CLIPBOARD_CLEAR)
  }

  getClipboard() {
    return this.clipboard
  }

  getClipboardInfo() {
    if (!this.clipboard) return null

    switch (this.clipboard.type) {
      case 'row':
        return {
          type: 'row',
          description: `Row: ${this.clipboard.data.hinmeiCode}-${this.clipboard.data.niukabango}`,
          count: this.clipboard.data.distribution_result?.length || 0
        }
      case 'column': {
        // Show plain customerCode in UI, not the full composite key
        const displayCode =
          this.clipboard.customerCode ||
          ColumnManager.parseColumnKey(this.clipboard.columnKey).customerCode
        return {
          type: 'column',
          description: `Column: ${displayCode}`,
          count: this.clipboard.data.length
        }
      }
      case 'cell':
        return {
          type: 'cell',
          description: `Cell: ${ColumnManager.parseColumnKey(this.clipboard.data.customerCode).customerCode}`,
          value: this.clipboard.data.value
        }
      default:
        return null
    }
  }
}

export default ClipboardManager
